import { SxProps, Theme } from '@mui/material';

/**
 * The three button weights the v4 detail pages use.
 *
 * They share one type scale (11px / 600 / sentence case) and one geometry, and
 * differ only in fill — so a screen can be read at a glance by how loud its
 * controls are:
 *
 * - **accent** commits. The Save in the sticky header, and nothing else on the
 *   page, should be filled.
 * - **ghost** reverses or steps aside — Cancel, Close, Discard.
 * - **quiet** grows a list. The Location tab has four `Add` buttons in four
 *   panel headers; as outlined pills they out-shouted the one Save that
 *   actually writes to the server.
 *
 * These started as three copy-pasted `accentBtnSx` consts in the summary bars.
 * Kept as `sx` objects rather than new `Button` variants because the `Button`
 * atom has hundreds of consumers across the legacy pages, and those still want
 * the uppercase MUI treatment.
 */

const focusRing = {
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'v4.accent',
    outlineOffset: 2,
  },
};

const base = {
  py: '5px',
  px: '12px',
  minWidth: 0,
  borderRadius: '6px',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '11px',
  lineHeight: 1.6,
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  ...focusRing,
};

/**
 * The commit action.
 *
 * `v4.accent` carries white text at only 3.74:1 — under the AA 4.5:1 bar for an
 * 11px label. `accentDark` is 5.47:1 and reads as the same teal.
 */
export const v4AccentButtonSx: SxProps<Theme> = {
  ...base,
  bgcolor: 'v4.accentDark',
  color: 'common.white',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'v4.accentDark', opacity: 0.9, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: 'v4.borderDark', color: 'common.white', opacity: 0.7 },
};

/** The reversing action — Cancel, Close, Discard. */
export const v4GhostButtonSx: SxProps<Theme> = {
  ...base,
  color: 'text.secondary',
  borderColor: 'v4.borderDark',
  '&:hover': { borderColor: 'v4.accentDark', color: 'v4.accentDark', bgcolor: 'transparent' },
};

/**
 * The additive action — `Add`, in a panel header.
 *
 * No border: it sits on the panel's own header rule, where a second outline
 * would be a box drawn inside a box.
 */
export const v4QuietButtonSx: SxProps<Theme> = {
  ...base,
  px: '8px',
  color: 'text.secondary',
  '& .MuiButton-startIcon': { mr: '4px', '& svg': { fontSize: 16 } },
  '&:hover': { color: 'v4.accentDark', bgcolor: 'v4.tableHover' },
  '&.Mui-disabled': { color: 'text.disabled' },
};

/**
 * A switch sitting in a panel header, beside the panel's own actions.
 *
 * MUI's default `FormControlLabel` sets its label at body1/14px, which next to
 * a 14px panel title and an 11px button reads as a third type size in a 40px
 * strip. This matches the label to the buttons around it and shrinks the switch
 * to suit.
 */
export const v4PanelToggleSx: SxProps<Theme> = {
  ml: 0,
  mr: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '11px',
    fontWeight: 600,
    color: 'text.secondary',
  },
};
